/**
 * Unified configuration for all chatbot services
 * Single source of truth for environment-specific settings
 */
import { CONVERSATION_MODES } from '../constants/modes';

/**
 * Environment detection
 */
export const getEnvironment = () => {
  return window.location.hostname === 'localhost' ? 'development' : 'production';
};

const getBaseUrl = {
  localhost: 'https://api-ai.tenant-ai-1.oneclicklabs.es',
  'edv.tenant-ai-1.oneclicklabs.es': 'https://api-ai.tenant-ai-1.oneclicklabs.es',
  'edv.tenant-ai-2.oneclicklabs.es': 'https://api-ai.tenant-ai-2.oneclicklabs.es',
  'tenant-ai-1.oneclicklabs.es': 'https://api-ai.tenant-ai-1.oneclicklabs.es',
  'tenant-ai-2.oneclicklabs.es': 'https://api-ai.tenant-ai-2.oneclicklabs.es',
  'sumuncampus-co.sumunthebestyou.com': 'https://ia.sumuncampus-co.sumunthebestyou.com',
  'santillana-integrations.oneclicklabs.es': 'https://api-ai.santillana-integrations.oneclicklabs.es',
  'edelvivesdigital-dev.oneclicklabs.es': 'https://api-ai.tenant-ai-1.oneclicklabs.es',
  'edelvivesdigital-qa.oneclicklabs.es': 'https://api-ai.tenant-ai-1.oneclicklabs.es',
  'edelvivesdigitalplus.com': 'https://edelvivesdigitalplus.com',
  'edelvivesdigitalplus.com.mx': 'https://edelvivesdigitalplus.com.mx',
  'tangerine-latest.oneclick.es': 'https://tangerine-latest.oneclick.es',
  'campus.modernacoreeducacao.com.br': 'https://ia.campus.modernacoreeducacao.com.br',
  'sumuncampus-co.tan-pre.santillana.com': 'https://ia.sumun-co-ia-pre.oneclick.es',
  'lms.unoi.com.mx': 'https://ia.lms.unoi.com.mx',
  'sumuncampus-cl.sumunthebestyou.com': 'https://ia.sumuncampus-cl.sumunthebestyou.com',
};

/**
 * Base configuration for all environments
 */
export const ENV_CONFIG = {
  development: {
    baseUrl: 'http://localhost:3000',
    apiPrefix: '/api/v1',
    defaultUserId: '6c10a08d-6ddf-40e9-8db5-a99588f14ff3', // Testing user ID
    isDev: true,
  },
  production: {
    baseUrl: getBaseUrl[window.location.hostname] || window.location.origin,
    apiPrefix: '/ai/v1',
    defaultUserId: null, // Must be provided in production
    isDev: false,
  },
};

/**
 * Get API URLs for current environment
 * @returns {Object} Object with URL builder functions
 */
export const getApiUrls = () => {
  const config = getEnvConfig();
  const baseUrl = `${config.baseUrl}${config.apiPrefix}`;

  return {
    // Chat URLs
    chat: {
      conversations: (mode) => `${baseUrl}/chat/${mode}`,
      conversation: (conversationId, mode) =>
        mode === CONVERSATION_MODES.TEACHER_TOOLS ? `${baseUrl}/chat/teacher-tools/conversations/${conversationId}` : `${baseUrl}/chat/${conversationId}`,
      messages: (conversationId) => `${baseUrl}/chat/${conversationId}`,
    },
    // Practice/Activities URLs
    activities: {
      generate: () => `${baseUrl}/activities/generate`,
      save: (activityInstanceId) => `${baseUrl}/activities/${activityInstanceId}`,
      start: (activityInstanceId) => `${baseUrl}/activities/${activityInstanceId}/start`,
      generateSession: () => `${baseUrl}/activities/sessions`,
      // Self-generated practice session from a lesson/unit selection.
      generateCustomSession: () => `${baseUrl}/activities/sessions/student-generated`,
      // Review session generated from a completed session's underperforming KCs.
      generateReviewSession: (sessionId) => `${baseUrl}/activities/sessions/${sessionId}/review`,
      // Auto-generated tutor practice for a single course, "Sugiéreme prácticas nuevas":
      // courseId travels in the URL, no request body.
      generateSuggestedSession: (courseId) => `${baseUrl}/activities/session/${encodeURIComponent(courseId)}`,
      // Volatile practice for a single blocked KC, launched from "Áreas de mejora"
      // (Mi progreso). `{ kcId, courseId }` travel in the body and the session is NOT
      // persisted as a recommendation, so it never shows up in "Hoy".
      generateKcSession: () => `${baseUrl}/activities/sessions/kc`,
      getSessions: () => `${baseUrl}/activities/sessions`,
      checkSessions: () => `${baseUrl}/activities/sessions/check`,
      getActivityInstance: (sessionId, activityInstanceId) => `${baseUrl}/activities/sessions/${sessionId}/activities/${activityInstanceId}`,
      discardSession: (sessionId) => `${baseUrl}/activities/sessions/${encodeURIComponent(sessionId)}/discard`,
      notifyError: () => `${baseUrl}/activities/notify-error`,
      generateOneActivityForText: () => `${baseUrl}/ai-activities/generate-one-activity-for-text`,
      generateOneActivity: () => `${baseUrl}/ai-activities/generate-one-activity`,
    },
    // Analytics URLs
    analytics: {
      classDiagnosticReport: (courseId) => `${baseUrl}/class-diagnostics/report${courseId ? `?courseId=${courseId}` : ''}`,
      eligibleSubjects: () => `${baseUrl}/subjects?hasActivities=true&includeDisciplines=true`,
      flaggedMessages: () => `${baseUrl}/stats/flagged-messages`,
      topicDetail: () => `${baseUrl}/stats/topic-detail`,
      topicRanking: () => `${baseUrl}/stats/topic-ranking`,
      flaggedMessageTypes: () => `${baseUrl}/stats/flagged-messages/types`,
      intentDistribution: () => `${baseUrl}/stats/intent-distribution`,
      useAnalytics: () => `${baseUrl}/stats/use`,
      annotationStatus: (annotationId) => `${baseUrl}/stats/annotations/${annotationId}`,
      annotations: () => `${baseUrl}/stats/annotations`,
      annotationViews: () => `${baseUrl}/stats/annotations/views`,
      userKcProgress: (userId, kcId) => `${baseUrl}/stats/users/${encodeURIComponent(userId)}/kcs/${encodeURIComponent(kcId)}/progress`,
      userKcActivities: (userId, kcId) => `${baseUrl}/stats/users/${encodeURIComponent(userId)}/kcs/${encodeURIComponent(kcId)}/activities`,
      userCourses: (userId) => `${baseUrl}/stats/users/${encodeURIComponent(userId)}/courses`,
      userCourseToc: (userId, courseId) => `${baseUrl}/stats/users/${encodeURIComponent(userId)}/courses/${encodeURIComponent(courseId)}/toc`,
      // Session-scoped ToC for the logged-in student (user resolved from the token),
      // distinct from the teacher endpoint above which takes an explicit userId.
      sessionCourseToc: (courseId) => `${baseUrl}/stats/user/courses/${encodeURIComponent(courseId)}/toc`,
      // Aggregated per-course progress for the logged-in student: one row per assigned
      // AI course in a single request, so the "Mapa de dominio" grid avoids a fan-out.
      sessionCoursesProgress: () => `${baseUrl}/stats/user/courses/progress`,
      // Most recently blocked KCs for the logged-in student (cross-course, top 10, newest
      // first): feeds the "Áreas de mejora" section from a single request.
      sessionBlockedKcs: () => `${baseUrl}/stats/user/kcs/blocked`,
      // Most recently mastered KCs for the logged-in student (cross-course, top 10, newest
      // first): feeds the "Mis fortalezas" section from a single request.
      sessionMasteredKcs: () => `${baseUrl}/stats/user/kcs/mastered`,
      userCourseSessions: (userId, courseId) => `${baseUrl}/stats/users/${encodeURIComponent(userId)}/courses/${encodeURIComponent(courseId)}/sessions`,
      activityInstance: (activityInstanceId) => `${baseUrl}/stats/activity-instances/${encodeURIComponent(activityInstanceId)}`,
      teacherStudentsByCourses: (courseIds) =>
        `${baseUrl}/stats/teacher/students-by-courses?${courseIds.map((id) => `courseIds=${encodeURIComponent(id)}`).join('&')}`,
    },
    // User Feedback URLs
    feedback: {
      submit: () => `${baseUrl}/feedback`,
    },
    // Teacher Review URLs
    teacherReview: {
      conversation: (conversationId) => `${baseUrl}/chat/teacher-review/conversations/${conversationId}`,
    },
    // Teacher Tools URLs
    teacherTools: {
      getContentsContent: () => `${baseUrl}/chat/teacher-tools/contents/content`,
      history: (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return `${baseUrl}/chat/teacher-tools/history${queryString ? `?${queryString}` : ''}`;
      },
      // Teacher Tools URLs
      realWorldConnection: () => `${baseUrl}/chat/teacher-tools/real-world-connection`,
      textLevelAdapter: () => `${baseUrl}/chat/teacher-tools/text-level-adaptation`,
      lessonPlanGenerator: () => `${baseUrl}/chat/teacher-tools/lesson-plan-generator`,
      rubricGenerator: () => `${baseUrl}/chat/teacher-tools/rubric-generator`,
      vocabularyCreator: () => `${baseUrl}/chat/teacher-tools/vocabulary-creator`,
      textTranslator: () => `${baseUrl}/chat/teacher-tools/text-translator`,
      studentFeedback: () => `${baseUrl}/chat/teacher-tools/student-feedback`,
      questionGenerator: () => `${baseUrl}/chat/teacher-tools/question-generator`,
      classroomDynamics: () => `${baseUrl}/chat/teacher-tools/classroom-dynamics`,
      activityTypes: (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return `${baseUrl}/activity-types${queryString ? `?${queryString}` : ''}`;
      },
    },
    // Conversations URLs
    conversations: {
      quizRevision: () => `${baseUrl}/chat/revision/conversations`,
      quizRevisionLookup: (quizJobGuid, questionReferenceId) => `${baseUrl}/chat/revision/jobs/${quizJobGuid}/questions/${questionReferenceId}/conversation`,
      quizRevisionRegenerate: () => `${baseUrl}/chat/revision/regenerate`,
      quizRevisionInvalidate: (quizJobGuid) => `${baseUrl}/chat/revision/jobs/${quizJobGuid}/invalidate`,
    },
    // Tutor Usage Statistics URLs
    tutorStats: {
      getConversationsByJobs: (jobGuids) => {
        const queryParams = jobGuids.map((guid) => `quizJobGuids=${encodeURIComponent(guid)}`).join('&');
        return `${baseUrl}/chat/revision/conversations?${queryParams}`;
      },
    },
    // User Data URLs
    userData: {
      courses: () => `${baseUrl}/data-manager/user/courses`,
      coursesToc: () => `${baseUrl}/data-manager/user/courses/toc`,
      coursesPractice: () => `${baseUrl}/data-manager/user/courses/practice`,
    },
    // Configurations URLs
    configurations: {
      getByKey: (key) => `${baseUrl}/data-manager/configurations?key=${encodeURIComponent(key)}`,
      getByKeys: (keys) => `${baseUrl}/data-manager/configurations?${keys.map((key) => `key[]=${encodeURIComponent(key)}`).join('&')}`,
    },
    // Data Manager URLs (regions, evaluation criteria, etc.)
    dataManager: {
      regions: (countryCode) => `${baseUrl}/data-manager/regions?countryCode=${encodeURIComponent(countryCode)}`,
      evaluationCriteria: (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return `${baseUrl}/data-manager/evaluation-criteria${queryString ? `?${queryString}` : ''}`;
      },
    },
  };
};

/**
 * Get configuration for current environment
 * @param {string} environment - Optional environment override
 * @returns {Object} Environment configuration
 */
export const getEnvConfig = (environment) => {
  const env = environment || getEnvironment();
  return ENV_CONFIG[env] || ENV_CONFIG.development;
};

/**
 * Get user ID from localStorage (shared logic)
 * @returns {string|null} User ID
 */
export const getUserIdFromStorage = () => {
  try {
    // Get user data from the persisted Redux store
    const persistedAuth = localStorage.getItem('persist:i2c_auth');
    if (persistedAuth) {
      const authData = JSON.parse(persistedAuth);
      if (authData && authData.user) {
        const userData = JSON.parse(authData.user);
        if (userData && userData.guid) {
          return userData.guid;
        }
      }
    }

    // Use default user ID as fallback
    const environment = getEnvironment();
    const config = getEnvConfig();
    const fallbackId = config.defaultUserId;

    // In production, log warning if no user ID is found
    if (environment === 'production' && !fallbackId) {
      console.warn('No user ID found and no fallback provided in production environment');
    }

    return fallbackId;
  } catch (error) {
    console.error('Error retrieving user ID:', error);
    const config = getEnvConfig();
    return config.defaultUserId;
  }
};

// Mirrors typesRoles.TEACHER in the host app's auth duck (core/store/auth.duck.js);
// the chatbot submodule must stay self-contained, so the value is duplicated here.
const TEACHER_ROLE_GUID = 'R02';

let lastPersistedAuthRaw = null;
let lastPersistedUser = null;

/**
 * Strict reader for session-scoped caches: the persisted authenticated user
 * object, or `null` when nobody is logged in.
 *
 * Unlike `getUserIdFromStorage`, this NEVER falls back to a default user —
 * cache keying needs "logged out" to be distinguishable from "dev fallback".
 * Memoized on the raw persisted slice so per-render calls stay cheap.
 *
 * @returns {Object|null} Persisted user object, or null when unauthenticated
 */
const getPersistedUser = () => {
  try {
    const persistedAuth = localStorage.getItem('persist:i2c_auth');
    if (persistedAuth === lastPersistedAuthRaw) {
      return lastPersistedUser;
    }
    lastPersistedAuthRaw = persistedAuth;
    lastPersistedUser = null;
    if (persistedAuth) {
      const authData = JSON.parse(persistedAuth);
      if (authData && authData.user) {
        lastPersistedUser = JSON.parse(authData.user) ?? null;
      }
    }
    return lastPersistedUser;
  } catch (error) {
    console.error('Error retrieving persisted user:', error);
    lastPersistedUser = null;
    return null;
  }
};

/**
 * The persisted authenticated user's guid, or `null` when nobody is logged in.
 * @returns {string|null}
 */
export const getPersistedUserGuid = () => getPersistedUser()?.guid ?? null;

/**
 * Whether the persisted authenticated user is an AI-enabled teacher — the only
 * audience for the tenant configuration flags. Mirrors the header gate
 * (`isTeacher(user) && !!user?.is_tangerine_ai`); for teachers,
 * `ChatbotContext.isAIEnabled` reduces to the same `is_tangerine_ai` check.
 *
 * @returns {boolean}
 */
export const isPersistedAiTeacher = () => {
  const user = getPersistedUser();
  return user?.role_guid === TEACHER_ROLE_GUID && !!user?.is_tangerine_ai;
};

/**
 * Get auth token from localStorage (similar to user ID retrieval)
 * @returns {string|null} Auth token
 */
export const getAuthTokenFromStorage = () => {
  try {
    // Get auth data from the persisted Redux store
    const persistedAuth = localStorage.getItem('persist:i2c_auth');

    if (persistedAuth) {
      const authData = JSON.parse(persistedAuth);

      if (authData && authData.authToken) {
        // Parse the token if it's stringified
        const token = typeof authData.authToken === 'string' && authData.authToken.startsWith('"') ? JSON.parse(authData.authToken) : authData.authToken;
        return token;
      }
    }
    return null;
  } catch (error) {
    console.error('Error retrieving auth token:', error);
    return null;
  }
};

/**
 * Error thrown when an authenticated chatbot request is attempted without a token.
 * Lets services short-circuit the network call instead of hitting the API and
 * getting a 401 (the backend throws UnauthorizedException when the header is missing).
 */
export class MissingAuthTokenError extends Error {
  constructor(message = 'Missing authorization token: skipping chatbot API request') {
    super(message);
    this.name = 'MissingAuthTokenError';
    this.isMissingAuthToken = true;
  }
}

/**
 * Whether an auth token is currently available.
 * Use this to guard requests outside of a try/catch.
 * @param {Function} customGetAuthToken - Optional custom auth token getter
 * @returns {boolean}
 */
export const hasAuthToken = (customGetAuthToken = null) => {
  const authToken = customGetAuthToken ? customGetAuthToken() : getAuthTokenFromStorage();
  return Boolean(authToken);
};

/**
 * Create common headers for API requests.
 * Throws MissingAuthTokenError when no token is available so callers (which build
 * headers synchronously inside their try/catch right before fetch) skip the request
 * entirely instead of triggering an avoidable 401 from the API.
 * @param {Object} additionalHeaders - Additional headers to include
 * @param {Function} customGetAuthToken - Optional custom auth token getter (for services that need override)
 * @returns {Object} Headers object
 * @throws {MissingAuthTokenError} When no auth token is available
 */
export const createHeaders = (additionalHeaders = {}, customGetAuthToken = null) => {
  // Get auth token - use custom getter if provided, otherwise use default
  const authToken = customGetAuthToken ? customGetAuthToken() : getAuthTokenFromStorage();

  if (!authToken) {
    throw new MissingAuthTokenError();
  }

  return {
    'Content-Type': 'application/json',
    ...additionalHeaders,
    Authorization: authToken,
  };
};

/**
 * Default error handler
 * @param {string} serviceName - Name of the service for logging
 * @param {Error} error - Error object
 */
export const defaultErrorHandler = (serviceName, error) => {
  // A missing token is an expected state (e.g. no active session yet), not a real
  // failure: stay quiet to avoid noisy console errors.
  if (error?.isMissingAuthToken) {
    return;
  }
  console.error(`${serviceName} error:`, error);
};

/**
 * Wrap an error handler so AbortErrors are skipped. AbortErrors come from callers
 * cancelling in-flight requests on unmount: they are expected lifecycle noise, not
 * real failures, so the wrapped handler stays quiet and lets the caller's catch
 * block rethrow them untouched.
 * @param {Function} handler - Error handler to wrap
 * @returns {Function} Abort-safe error handler
 */
export const createAbortSafeErrorHandler = (handler) => (error) => {
  if (error?.name === 'AbortError') {
    return;
  }
  handler(error);
};
